import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, X } from "lucide-react";
import { api, formatError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const EMPTY = { title: "", date: "", content: "", image_url: "", link: "" };

export default function NewsManagement() {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/news"); setItems(data); }
    catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editingId) await api.put(`/news/${editingId}`, form);
      else await api.post("/news", form);
      toast.success(editingId ? "Updated" : "Added"); setShowForm(false); load();
    } catch (err) { toast.error(formatError(err)); }
  };

  const add = () => { setEditingId(null); setForm(EMPTY); setShowForm(true); };
  const edit = (n) => { setEditingId(n.id); setForm({ title: n.title, date: n.date, content: n.content, image_url: n.image_url || "", link: n.link || "" }); setShowForm(true); };
  const remove = async (n) => {
    if (!window.confirm(`Delete "${n.title}"?`)) return;
    try { await api.delete(`/news/${n.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="news-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#DC2A1C] font-semibold mb-1">Communications</div>
          <h1 className="text-3xl font-black tracking-tight">News & Announcements</h1>
        </div>
        {isAdmin && <button onClick={add} className="tl-btn flex items-center gap-2" data-testid="add-news-btn"><Plus className="w-4 h-4" /> New Post</button>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="news-grid">
        {items.map((n) => (
          <div key={n.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden flex flex-col">
            {n.image_url && <div className="aspect-[16/9] overflow-hidden"><img src={n.image_url} alt={n.title} className="w-full h-full object-cover" /></div>}
            <div className="p-5 flex-1 flex flex-col">
              <div className="text-[11px] uppercase tracking-[0.15em] text-[#DC2A1C] font-semibold mb-2">{n.date}</div>
              <h3 className="font-semibold mb-2 leading-snug">{n.title}</h3>
              <p className="text-sm text-gray-600 line-clamp-3 flex-1">{n.content}</p>
              {isAdmin && (
                <div className="flex gap-2 mt-4 pt-4 border-t border-gray-100">
                  <button onClick={() => edit(n)} className="text-xs flex items-center gap-1 text-gray-600 hover:text-[#DC2A1C]"><Edit2 className="w-3 h-3" /> Edit</button>
                  <button onClick={() => remove(n)} className="text-xs flex items-center gap-1 text-red-600 hover:text-red-700"><Trash2 className="w-3 h-3" /> Delete</button>
                </div>
              )}
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="col-span-full text-center py-10 text-gray-500 text-sm">No news yet.</div>}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 grid place-items-center p-4" onClick={() => setShowForm(false)}>
          <form className="bg-white rounded-xl p-6 w-full max-w-lg space-y-4" onClick={(e) => e.stopPropagation()} onSubmit={save} data-testid="news-form">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold">{editingId ? "Edit" : "New"} News</h3>
              <button type="button" onClick={() => setShowForm(false)}><X className="w-5 h-5" /></button>
            </div>
            <InputF label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} required />
            <InputF label="Date" type="date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} required />
            <InputF label="Image URL (optional)" value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} />
            <InputF label="Read-more Link (optional)" value={form.link} onChange={(v) => setForm({ ...form, link: v })} />
            <div>
              <label className="block text-[11px] uppercase tracking-[0.12em] font-semibold text-gray-500 mb-1">Content</label>
              <textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} required rows="5" className="w-full bg-[#F8F9FA] border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#DC2A1C]" />
            </div>
            <button type="submit" className="tl-btn w-full" data-testid="news-form-submit">{editingId ? "Update" : "Publish"}</button>
          </form>
        </div>
      )}
    </div>
  );
}

function InputF({ label, value, onChange, type = "text", required }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.12em] font-semibold text-gray-500 mb-1">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} className="w-full bg-[#F8F9FA] border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#DC2A1C]" />
    </div>
  );
}
